import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  FaUsers, 
  FaSchool, 
  FaBlog, 
  FaCalendarAlt, 
  FaCreditCard, 
  FaTachometerAlt,
  FaBars,
  FaSignOutAlt,
  FaUserShield,
  FaTags,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
  FaCog
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AdminRoutes from '../../routes/adminRoutes';

/**
 * Admin Panel layout component
 * Provides navigation and layout structure for all admin pages
 */
const AdminPanel = () => {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  
  // Verify user is authenticated as an admin
  useEffect(() => {
    if (!loading && (!isAuthenticated || (user && user.role !== 'system_admin'))) {
      showToast('You do not have permission to access the admin panel', 'error');
      navigate('/login');
    }
  }, [user, isAuthenticated, loading, navigate, showToast]);
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  const toggleMinimize = () => {
    setSidebarMinimized(!sidebarMinimized);
  };
  
  const handleLogout = () => {
    logout();
    showToast('Logged out successfully', 'success');
    navigate('/login');
  };
  
  // Navigation links configuration
  const navLinks = [
    { to: '/admin/dashboard', icon: <FaTachometerAlt />, label: 'Dashboard' },
    { to: '/admin/admins', icon: <FaUserShield />, label: 'Administrators' },
    { to: '/admin/schools', icon: <FaSchool />, label: 'Schools' },
    { to: '/admin/blogs', icon: <FaBlog />, label: 'Blog Management' },
    { to: '/admin/events', icon: <FaCalendarAlt />, label: 'Events' },
    { to: '/admin/event-registrations', icon: <FaCalendarAlt />, label: 'Event Registrations' },
    { to: '/admin/bulk-messaging', icon: <FaCalendarAlt />, label: 'Bulk Messaging' },
    { to: '/admin/subscriptions', icon: <FaCreditCard />, label: 'Subscriptions' },
    { to: '/admin/discount-codes', icon: <FaTags />, label: 'Discount Codes' },
    { to: '/admin/profile', icon: <FaUser />, label: 'My Profile' },
    { to: '/admin/settings', icon: <FaCog />, label: 'Settings' }
  ];
  
  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  // Only render the panel if user is authenticated as admin
  if (!isAuthenticated || !user || user.role !== 'system_admin') {
    return null; // The redirect will happen in the useEffect
  }
  
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Helmet>
        <title>Admin Panel | Meira</title>
      </Helmet>
      
      {/* Header */}
      <header className="bg-primary text-white shadow-md">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={toggleSidebar}
              className="mr-4 lg:hidden p-1 rounded hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-white"
            >
              <FaBars />
            </button>
            <h1 className="text-xl font-bold">Meira Admin</h1>
          </div>
          
          <div className="flex items-center">
            <div className="mr-4 text-right hidden sm:block">
              <p className="font-medium">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs opacity-80">System Administrator</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-white flex items-center"
              title="Logout"
            >
              <FaSignOutAlt />
              <span className="ml-1 hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>
      
      <div className="flex flex-1">
        {/* Sidebar */}
        <aside 
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } ${
            sidebarMinimized ? 'lg:w-16' : 'lg:w-64'
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-30 bg-white shadow transform transition-all duration-300 ease-in-out h-[calc(100vh-64px)] mt-16`}
        >
          <div className="h-full flex flex-col">
            <div className="flex justify-end p-2 border-b">
              <button 
                onClick={toggleMinimize}
                className="p-1 rounded-md text-gray-500 hover:bg-gray-100 hover:text-primary focus:outline-none hidden lg:block"
              >
                {sidebarMinimized ? <FaChevronRight /> : <FaChevronLeft />}
              </button>
            </div>
            
            <nav className="h-full overflow-y-auto py-4 px-3">
              <ul className="space-y-1">
                {navLinks.map((link) => (
                  <li key={link.to}>
                    <NavLink
                      to={link.to}
                      className={({ isActive }) => 
                        `flex items-center ${sidebarMinimized ? 'justify-center' : 'px-4'} py-3 rounded-md transition-colors ${
                          isActive
                            ? 'bg-primary-50 text-primary' 
                            : 'text-gray-700 hover:bg-gray-100'
                        }`
                      }
                      title={sidebarMinimized ? link.label : ''}
                    >
                      <span className={`text-lg ${sidebarMinimized ? '' : 'mr-3'}`}>{link.icon}</span>
                      {!sidebarMinimized && <span>{link.label}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>
        
        {/* Main content */}
        <main className={`flex-1 p-4 lg:p-6 transition-all duration-300`}>
          {/* Backdrop for mobile */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
              onClick={toggleSidebar}
            ></div>
          )}
          
          {/* Page content from child routes */}
          <AdminRoutes />
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
